import React from "react";
import { IconChevronDown, IconSearch, IconX } from "@tabler/icons-react";
import clsx from "clsx";
import DebouncedInput from "components/DebounceInput";
import { IconObject, IconProduct } from "utils";

interface PropertyInputProps {
  value?: string | number;
  onRemoveClicked: () => void;
  onChangeSearch?: (value: string) => void;
  inputClassName?: string;
  placeholder?: string;
  isObject?: boolean;
  isProduct?: boolean;
  hasError?: boolean;
  type?: "text" | "number";
  searchMode?: boolean;
  onChangeValue?: (value: string) => void;
  changeFocus?: () => void;
  inputRef?: React.RefObject<HTMLInputElement>;
}

const PropertyInput = ({ ...props }: PropertyInputProps) => {
  const objectIcon = (
    <div className="bg-purple-50 text-purple-500 h-full flex items-center justify-center w-full">
      {IconObject}
    </div>
  );

  const productIcon = (
    <div className="bg-teal-50 text-teal-500 h-full flex items-center justify-center w-full">
      {IconProduct}
    </div>
  );

  const onClickRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    props.onRemoveClicked();
  };

  const onSubmitValue = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      props.onChangeValue?.(String(e.currentTarget.value));
      props.onChangeSearch?.("");
      props.inputRef?.current?.blur();
    } else if (e.key === "ArrowDown") {
      props.changeFocus?.();
      props.inputRef?.current?.blur();
    }
  };

  function getIcon(): React.ReactNode {
    if (props.searchMode && !props.value) {
      return (
        <IconSearch
          className={props.hasError ? "text-red-600" : "text-gray-500"}
          size={17}
        />
      );
    }
    if (props.value) {
      return (
        <IconX
          size={20}
          onClick={onClickRemove}
          className="pointer-events-auto cursor-pointer text-gray-500 hover:text-gray-800"
        />
      );
    }
    if (!props.value && props.type === "number") {
      return <></>;
    }
    return (
      <IconChevronDown
        className={props.hasError ? "text-red-600" : "text-gray-500"}
        size={17}
      />
    );
  }

  return (
    <DebouncedInput
      ref={props.inputRef}
      height="middle"
      className={clsx(
        "bg-white border-gray-300 border rounded-md",
        "flex justify-between px-2 items-center text-gray-900 w-full",
        props.inputClassName
      )}
      containerClassName="w-full"
      inputWrapperClassName={props.searchMode ? "border-indigo-500" : ""}
      placeholder={props.placeholder}
      value={props.value || ""}
      leadingIcon={
        props.isObject ? objectIcon : props.isProduct ? productIcon : null
      }
      icon={getIcon()}
      iconClassName="!right-2"
      onChange={(value) => props.onChangeSearch?.(String(value))}
      hasError={props.hasError}
      type={props.type}
      onKeyDown={onSubmitValue}
    />
  );
};

export default PropertyInput;
